import React, { useState, useMemo, useRef } from "react"
import { Link } from "react-router-dom"
import { Table, Pagination } from "react-bootstrap"
import { CSSTransition } from "react-transition-group"
import { validateDate } from "../commonComponents/EventHandler"
import "./Pagination.css"

const SearchResults = ({ results, loading }) => {
   var inProp = true
   const [currentPage, setCurrentPage] = useState(1)
   const [resultsPerPage, setResultsPerPage] = useState(20)
   const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" })
   const nodeRef = useRef(null)

   function addLinkData(linkKey, link) {
      var arr = []
      let searchItems = {}

      searchItems["name"] = linkKey
      searchItems["link"] = link
      arr.push(searchItems)
      sessionStorage.removeItem("breadCrumb")
      sessionStorage.setItem("breadCrumb", btoa(JSON.stringify(arr)))
   }

   const renderMapUrl = (result) =>
      result.link != null && result.link !== "" ? (
         <Link
            onClick={() =>
               addLinkData(result.name, `/${result.link.toUpperCase()}/${result.id}`)
            }
            to={`/${result.link.toUpperCase()}/${result.id}`}
         >
            <span>{result.name}</span>
         </Link>
      ) : (
         result.name
      )

   const requestSort = (key) => {
      let direction = "ascending"
      if (sortConfig.key === key && sortConfig.direction === "ascending") {
         direction = "descending"
      }
      setSortConfig({ key, direction })
   }

   const getClassNamesFor = (name) => {
      if (!sortConfig.key) {
         return "fa fa-sort"
      } else if (sortConfig.key === name) {
         if (sortConfig.direction === "ascending") {
            return "fa fa-caret-up"
         } else {
            return "fa fa-caret-down"
         }
      } else {
         return "fa fa-sort"
      }
   }

   const sortedResults = useMemo(() => {
      let sortableResults = [...results]
      if (sortConfig.key !== null) {
         sortableResults.sort((a, b) => {
            let aValue = a[sortConfig.key]
            let bValue = b[sortConfig.key]

            // Handle date fields
            if (
               sortConfig.key === "lastModification" ||
               sortConfig.key === "releasedate" ||
               sortConfig.key === "creationDate"
            ) {
               aValue = aValue ? new Date(aValue) : new Date(0)
               bValue = bValue ? new Date(bValue) : new Date(0)
            }

            if (aValue < bValue) {
               return sortConfig.direction === "ascending" ? -1 : 1
            }
            if (aValue > bValue) {
               return sortConfig.direction === "ascending" ? 1 : -1
            }
            return 0
         })
      }
      return sortableResults
   }, [results, sortConfig])

   // Pagination
   const indexOfLastResult = currentPage * resultsPerPage
   const indexOfFirstResult = indexOfLastResult - resultsPerPage
   const currentResults = sortedResults.slice(indexOfFirstResult, indexOfLastResult)

   const handlePageChange = (pageNumber) => {
      setCurrentPage(pageNumber)
   }

   const handlePageLengthChange = (e) => {
      setResultsPerPage(Number(e.target.value))
      setCurrentPage(1)
   }

   const totalPages = Math.ceil(sortedResults.length / resultsPerPage)

   const renderPaginationItems = () => {
      const items = []
      const maxPagesToShow = 5
      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2))
      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)

      if (endPage - startPage + 1 < maxPagesToShow) {
         startPage = Math.max(1, endPage - maxPagesToShow + 1)
      }

      if (currentPage > 1) {
         items.push(
            <Pagination.First key="first" onClick={() => handlePageChange(1)} />,
            <Pagination.Prev key="prev" onClick={() => handlePageChange(currentPage - 1)} />
         )
      }

      for (let page = startPage; page <= endPage; page++) {
         items.push(
            <Pagination.Item
               key={page}
               active={page === currentPage}
               onClick={() => handlePageChange(page)}
            >
               {page}
            </Pagination.Item>
         )
      }

      if (currentPage < totalPages) {
         items.push(
            <Pagination.Next key="next" onClick={() => handlePageChange(currentPage + 1)} />,
            <Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} />
         )
      }

      return items
   }

   return (
      <div className="searchScrollbar">
         <CSSTransition in={inProp} timeout={500} classNames="my-node" nodeRef={nodeRef}>
            <div ref={nodeRef}>
               <div
                  style={{
                     marginBottom: "10px",
                     display: "flex",
                     justifyContent: "space-between",
                     alignItems: "center",
                  }}
               >
                  <div>
                     <label>
                        Show{" "}
                        <select value={resultsPerPage} onChange={handlePageLengthChange}>
                           <option value={20}>20</option>
                           <option value={50}>50</option>
                           <option value={100}>100</option>
                        </select>{" "}
                        entries
                     </label>
                  </div>
                  <div>
                     {sortedResults.length > 0 && (
                        <span>
                           Showing {indexOfFirstResult + 1} to{" "}
                           {Math.min(indexOfLastResult, sortedResults.length)} of{" "}
                           {sortedResults.length} entries
                        </span>
                     )}
                  </div>
               </div>

               <Table bordered hover responsive>
                  <thead>
                     <tr>
                        <th
                           className="text-center"
                           onClick={() => requestSort("name")}
                           style={{ cursor: "pointer" }}
                        >
                           Name <i className={getClassNamesFor("name")}></i>
                        </th>
                        <th
                           className="colWidth"
                           onClick={() => requestSort("revision")}
                           style={{ cursor: "pointer" }}
                        >
                           Revision <i className={getClassNamesFor("revision")}></i>
                        </th>
                        <th
                           className="colWidth"
                           onClick={() => requestSort("displaytype")}
                           style={{ cursor: "pointer" }}
                        >
                           Type <i className={getClassNamesFor("displaytype")}></i>
                        </th>
                        <th
                           className="preserveSpace"
                           onClick={() => requestSort("title")}
                           style={{ cursor: "pointer" }}
                        >
                           Title <i className={getClassNamesFor("title")}></i>
                        </th>
                        <th
                           className="preserveSpace colDiscWidth"
                           onClick={() => requestSort("description")}
                           style={{ cursor: "pointer" }}
                        >
                           Description <i className={getClassNamesFor("description")}></i>
                        </th>
                        <th
                           onClick={() => requestSort("maturityState")}
                           style={{ cursor: "pointer" }}
                        >
                           Maturity State{" "}
                           <i className={getClassNamesFor("maturityState")}></i>
                        </th>
                        <th
                           onClick={() => requestSort("lastModification")}
                           style={{ cursor: "pointer" }}
                        >
                           Last Modified Date{" "}
                           <i className={getClassNamesFor("lastModification")}></i>
                        </th>
                        <th
                           onClick={() => requestSort("releasedate")}
                           style={{ cursor: "pointer" }}
                        >
                           Release Date{" "}
                           <i className={getClassNamesFor("releasedate")}></i>
                        </th>
                        <th
                           onClick={() => requestSort("creationDate")}
                           style={{ cursor: "pointer" }}
                        >
                           Creation Date{" "}
                           <i className={getClassNamesFor("creationDate")}></i>
                        </th>
                        <th
                           onClick={() => requestSort("originator")}
                           style={{ cursor: "pointer" }}
                        >
                           Originator <i className={getClassNamesFor("originator")}></i>
                        </th>
                     </tr>
                  </thead>
                  <tbody>
                     {currentResults.length > 0 ? (
                        currentResults.map((result) => (
                           <tr key={`${result.id}-${result.name}-${result.revision}`}>
                              <td className="text-center">{renderMapUrl(result)}</td>
                              <td className="colWidth">{result.revision}</td>
                              <td className="colWidth">{result.displaytype}</td>
                              <td className="preserveSpace">{result.title}</td>
                              <td className="preserveSpace colDiscWidth">
                                 {result.description}
                              </td>
                              <td>{result.maturityState}</td>
                              <td>{validateDate(result.lastModification)}</td>
                              <td>{validateDate(result.releasedate)}</td>
                              <td>{validateDate(result.creationDate)}</td>
                              <td>{result.originator}</td>
                           </tr>
                        ))
                     ) : (
                        <tr>
                           <td
                              colSpan="10"
                              style={{
                                 textAlign: "center",
                                 color: "#0173d4",
                                 fontSize: "18px",
                              }}
                           >
                              No Records Found.
                           </td>
                        </tr>
                     )}
                  </tbody>
               </Table>

               {sortedResults.length > resultsPerPage && (
                  <div
                     style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "10px",
                     }}
                  >
                     <Pagination>{renderPaginationItems()}</Pagination>
                  </div>
               )}
            </div>
         </CSSTransition>
      </div>
   )
}

export default SearchResults